import React, { useState } from 'react';

import { signInWithEmailAndPassword, signOut } from 'firebase/auth';

import { Link, Navigate, useNavigate } from 'react-router-dom';

import { auth, db } from '../../firebase/firebase-config';

import { doc, setDoc, updateDoc } from 'firebase/firestore';

import { Form, Button, Card, Alert, Image, Spinner } from 'react-bootstrap';

import { useUser } from '../../context/UserContext';

import { useTenant } from '../../context/TenantContext';

import { formatNoMembershipMessage } from '../../platform/membershipMessages';
import { resolveBrandingColors } from '../../platform/brandingColors';

import {

  canAccessTenant,

  loadMembershipsForUser,

} from '../../services/membershipService';

import User from '../../models/User';
import { fetchUserProfileSnapshot } from '../../services/userProfileService';
import { resolveFirebaseAuthError } from '../../platform/authErrorMessages';
import {
  PASSWORD_RESET_SENT_MESSAGE,
  sendPasswordResetForEmail,
} from '../../services/passwordResetService';
import {
  EMAIL_NOT_FOUND_MESSAGE,
  lookupForgottenEmail,
} from '../../services/emailRecoveryService';

import './Login.css';



function Login() {

  const [email, setEmail] = useState('');

  const [password, setPassword] = useState('');

  const [error, setError] = useState('');

  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('login');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [recoveryFirstName, setRecoveryFirstName] = useState('');
  const [recoveryLastName, setRecoveryLastName] = useState('');
  const [recoveryPhone, setRecoveryPhone] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [emailRecoveryLoading, setEmailRecoveryLoading] = useState(false);

  const navigate = useNavigate();

  const { setUser, setBlockingTenantLogin } = useUser();

  const { tenantId, config, configLoading, hasSelectedOrganization } = useTenant();



  if (!hasSelectedOrganization) {

    return <Navigate to="/select-organization" replace />;

  }



  const { branding } = config;
  const { primaryColor } = resolveBrandingColors(branding);



  const createOrFetchProfile = async (uid, userEmail) => {
    const docRef = doc(db, 'users', uid);
    const docSnap = await fetchUserProfileSnapshot(uid);

    if (!docSnap.exists()) {

      const newUserProfile = new User({

        uid,

        firstName: '',

        lastName: '',

        displayName: '',

        email: userEmail,

        role: 'user',

        unit: '',

        phone: '',

      });



      await setDoc(docRef, newUserProfile.toFirestore());

      return newUserProfile;

    }



    const userData = docSnap.data();

    if (!userData.email) {

      await updateDoc(docRef, { email: userEmail });

    }



    return User.fromFirestore(docSnap);

  };



  const handleLogin = async (event) => {

    event.preventDefault();

    const selectedTenantId = tenantId;
    const selectedDisplayName = config.displayName;

    setLoading(true);

    setError('');

    setBlockingTenantLogin(true);

    try {

      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      const firebaseUser = userCredential.user;

      let memberships = await loadMembershipsForUser(firebaseUser.uid);
      if (!memberships.length) {
        await new Promise((resolve) => { setTimeout(resolve, 250); });
        memberships = await loadMembershipsForUser(firebaseUser.uid);
      }

      if (!canAccessTenant(memberships, selectedTenantId)) {

        await signOut(auth);

        setUser(null);

        setError(formatNoMembershipMessage(selectedDisplayName));

        return;

      }



      const userProfile = await createOrFetchProfile(firebaseUser.uid, firebaseUser.email);

      setUser(userProfile);
      navigate('/', { replace: true });

    } catch (loginError) {

      console.error('Login error:', loginError);

      setError(resolveFirebaseAuthError(loginError, { context: 'login' }));

    } finally {

      setBlockingTenantLogin(false);

      setLoading(false);

    }

  };



  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setForgotLoading(true);
    setError('');
    setForgotSuccess('');

    try {
      await sendPasswordResetForEmail(email);
      setForgotSuccess(PASSWORD_RESET_SENT_MESSAGE);
    } catch (resetError) {
      setError(resetError.message || 'Pošiljanje povezave ni uspelo.');
    } finally {
      setForgotLoading(false);
    }
  };

  const openForgotPassword = () => {
    setView('forgot');
    setError('');
    setForgotSuccess('');
    setEmailHint('');
  };

  const openForgotEmail = () => {
    setView('forgot-email');
    setError('');
    setForgotSuccess('');
    setEmailHint('');
  };

  const backToLogin = () => {
    setView('login');
    setError('');
    setForgotSuccess('');
    setEmailHint('');
  };

  const handleForgotEmail = async (event) => {
    event.preventDefault();
    setEmailRecoveryLoading(true);
    setError('');
    setEmailHint('');

    try {
      const result = await lookupForgottenEmail({
        tenantId,
        firstName: recoveryFirstName,
        lastName: recoveryLastName,
        phone: recoveryPhone,
      });

      if (result?.found && result.maskedEmail) {
        setEmailHint(result.maskedEmail);
      } else {
        setError(EMAIL_NOT_FOUND_MESSAGE);
      }
    } catch (recoveryError) {
      setError(recoveryError.message);
    } finally {
      setEmailRecoveryLoading(false);
    }
  };

  const loginViewTitle = (() => {
    if (view === 'forgot') return 'Pozabljeno geslo';
    if (view === 'forgot-email') return 'Pozabljen elektronski naslov';
    return 'Prijava';
  })();



  if (configLoading || !config) {

    return (

      <div className="login-container">

        <Spinner animation="border" role="status">

          <span className="visually-hidden">Nalagam...</span>

        </Spinner>

      </div>

    );

  }



  return (

    <div className="login-container">

      <Card className="login-card">

        <Card.Body>

          <div className="text-center mb-4">

            <Image

              src={branding.logoPath}

              alt={branding.logoAlt || config.displayName}

              className="login-logo"

            />

          </div>

          <Card.Title
            className="mb-4 text-center"
            style={{ color: primaryColor }}
          >
            {config.displayName}
          </Card.Title>

          <p className="login-subtitle text-center text-muted mb-4">
            {loginViewTitle}
          </p>

          {error && (

            <Alert variant="danger" style={{ whiteSpace: 'pre-line' }}>

              {error}

            </Alert>

          )}

          {forgotSuccess && (

            <Alert variant="success">

              {forgotSuccess}

            </Alert>

          )}

          {emailHint && (

            <Alert variant="success">

              Vaš prijavni elektronski naslov: <strong>{emailHint}</strong>

            </Alert>

          )}

          {view === 'login' ? (
          <Form onSubmit={handleLogin}>

            <Form.Group className="mb-3" controlId="formBasicEmail">
              <div className="login-field-label">
                <Form.Label className="mb-0">Elektronski naslov</Form.Label>
                <button
                  type="button"
                  className="login-inline-link"
                  onClick={openForgotEmail}
                >
                  Pozabljen elektronski naslov?
                </button>
              </div>
              <Form.Control
                type="email"
                placeholder="vas@email.si"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formBasicPassword">
              <div className="login-field-label">
                <Form.Label className="mb-0">Geslo</Form.Label>
                <button
                  type="button"
                  className="login-inline-link"
                  onClick={openForgotPassword}
                >
                  Pozabljeno geslo?
                </button>
              </div>
              <Form.Control
                type="password"
                placeholder="Geslo"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Form.Group>

            <Button
              type="submit"
              variant="primary"
              className="w-100"
              disabled={loading}
            >

              {loading ? 'Nalagam...' : 'Prijava'}

            </Button>

          </Form>
          ) : view === 'forgot' ? (
          <Form onSubmit={handleForgotPassword}>
            <p className="text-muted small mb-3">
              Vnesite elektronski naslov računa. Poslali vam bomo povezavo za nastavitev novega gesla.
            </p>

            <Form.Group className="mb-3" controlId="formForgotEmail">
              <Form.Label>Elektronski naslov</Form.Label>
              <Form.Control
                type="email"
                placeholder="vas@email.si"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Form.Group>

            <Button
              type="submit"
              variant="primary"
              className="w-100"
              disabled={forgotLoading}
            >
              {forgotLoading ? 'Pošiljam...' : 'Pošlji povezavo'}
            </Button>

            <div className="login-back">
              <button
                type="button"
                className="login-inline-link"
                onClick={backToLogin}
              >
                Nazaj na prijavo
              </button>
            </div>
          </Form>
          ) : (
          <Form onSubmit={handleForgotEmail}>
            <p className="text-muted small mb-3">
              Vnesite ime, priimek in telefonsko številko iz vašega profila.
              Prikazali bomo delno zakrit elektronski naslov vašega računa.
            </p>

            <Form.Group className="mb-3" controlId="formRecoveryFirstName">
              <Form.Label>Ime</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ime"
                value={recoveryFirstName}
                onChange={(e) => setRecoveryFirstName(e.target.value)}
                required
                autoComplete="given-name"
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formRecoveryLastName">
              <Form.Label>Priimek</Form.Label>
              <Form.Control
                type="text"
                placeholder="Priimek"
                value={recoveryLastName}
                onChange={(e) => setRecoveryLastName(e.target.value)}
                required
                autoComplete="family-name"
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="formRecoveryPhone">
              <Form.Label>Telefon</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Telefonska številka"
                value={recoveryPhone}
                onChange={(e) => setRecoveryPhone(e.target.value)}
                required
                autoComplete="tel"
              />
            </Form.Group>

            <Button
              type="submit"
              variant="primary"
              className="w-100"
              disabled={emailRecoveryLoading}
            >
              {emailRecoveryLoading ? 'Iskanje...' : 'Poišči elektronski naslov'}
            </Button>

            <div className="login-back">
              <button
                type="button"
                className="login-inline-link"
                onClick={backToLogin}
              >
                Nazaj na prijavo
              </button>
            </div>
          </Form>
          )}

          {view === 'login' && (
          <div className="login-footer">
            <Link to="/select-organization" className="login-footer__link">
              Spremeni organizacijo
            </Link>
          </div>
          )}

        </Card.Body>

      </Card>

    </div>

  );

}



export default Login;

